"use client";

import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/fun-ui/Button";
import {
  ChevronDownIcon,
  DeleteIcon,
  FileSignatureIcon,
  LoadingIcon,
  PlusIcon,
  RenameIcon,
} from "@/components/fun-ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/fun-ui/Popover";
import { Tip } from "@/components/fun-ui/Tip";
import { I18nProvider } from "@/components/i18n/I18nProvider";
import { useWriterI18n } from "@/hooks/useI18n";
import { cn } from "@/lib/utils";
import {
  isProtectedWriterFixtureProject,
  readWriterSidebarState,
  requestWriterSidebarAction,
  subscribeWriterSidebarState,
} from "./writer-sidebar-bridge";
import { handleWriterProjectMenuKeyboard } from "./writer-project-menu-keyboard";

type WriterSidebarMenuProps = {
  active: boolean;
  collapsed: boolean;
};

function WriterSidebarMenuContent({ active, collapsed }: WriterSidebarMenuProps) {
  const { t } = useWriterI18n();
  const path = usePathname();
  const router = useRouter();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(false);
  const [state, setState] = useState(readWriterSidebarState);

  useEffect(() => subscribeWriterSidebarState(setState), []);

  useEffect(() => {
    if (!collapsed) setOpen(false);
  }, [collapsed]);

  const projects = useMemo(() => state.projects.filter(({ id }) => (
    !isProtectedWriterFixtureProject(id)
    || id === "writer-fixture-academic"
    || id === state.activeProjectId
  )), [state.activeProjectId, state.projects]);

  const openWriter = () => {
    setOpen(false);
    router.push("/writer");
  };

  const navigateWriter = () => {
    router.push("/writer");
  };

  const requestCreate = () => {
    requestWriterSidebarAction({ type: "create" });
    setOpen(false);
    if (path !== "/writer") router.push("/writer");
  };

  const requestDelete = (projectId: string) => {
    requestWriterSidebarAction({ projectId, type: "delete" });
    setOpen(false);
    if (path !== "/writer") router.push(`/writer?projectId=${encodeURIComponent(projectId)}`);
  };

  const requestRename = (projectId: string, title: string) => {
    requestWriterSidebarAction({ projectId, title, type: "rename" });
    setOpen(false);
    if (path !== "/writer") router.push(`/writer?projectId=${encodeURIComponent(projectId)}`);
  };

  const requestSwitch = (projectId: string) => {
    if (projectId === state.activeProjectId || state.busy) return;
    requestWriterSidebarAction({ projectId, type: "switch" });
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    handleWriterProjectMenuKeyboard({
      close: () => setOpen(false),
      event,
      trigger: triggerRef.current,
    });
  };

  const renderProjectList = (isPopover = false) => projects.length ? (
    <div className={cn(
      "max-h-[212px] space-y-1 overflow-y-auto pr-1",
      !isPopover && "ml-8 mt-2",
    )}>
      {projects.map((project) => {
        const selected = project.id === state.activeProjectId;
        const switching = project.id === state.switchingProjectId;
        const protectedFixture = isProtectedWriterFixtureProject(project.id);
        return (
          <div className={cn("group flex items-center rounded-md", selected && "bg-muted")} key={project.id}>
            <button
              aria-current={selected ? "page" : undefined}
              className={cn("min-w-0 flex-1 truncate px-2 py-2 text-left text-xs text-muted-foreground hover:text-foreground", selected && "font-medium text-primary")}
              disabled={state.busy && !switching}
              onClick={() => {
                requestSwitch(project.id);
                if (isPopover) setOpen(false);
              }}
              title={project.title}
              type="button"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {switching ? <LoadingIcon size={12} /> : null}
                <span className="min-w-0 truncate">{project.title}</span>
              </span>
            </button>
            <Button
              aria-label={t("project.renameTitleWithName", { title: project.title })}
              className="h-7 w-7 shrink-0 p-0 text-muted-foreground opacity-0 hover:text-primary group-hover:opacity-100 focus-visible:opacity-100"
              disabled={state.busy}
              onClick={() => requestRename(project.id, project.title)}
              size="icon"
              type="button"
              variant="ghost"
            >
              <RenameIcon size={13} />
            </Button>
            {!protectedFixture ? (
              <Button
                aria-label={t("project.deleteTitle", { title: project.title })}
                className="mr-1 h-7 w-7 shrink-0 p-0 text-muted-foreground opacity-0 hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                disabled={state.busy}
                onClick={() => requestDelete(project.id)}
                size="icon"
                type="button"
                variant="ghost"
              >
                <DeleteIcon size={13} />
              </Button>
            ) : (
              <span className="mr-1 shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] text-annotation-foreground">
                {t("project.fixture")}
              </span>
            )}
          </div>
        );
      })}
    </div>
  ) : (
    <p className={cn("px-2 py-2 text-xs text-annotation-foreground", !isPopover && "ml-8 mt-2")}>
      {t("project.empty")}
    </p>
  );

  const createProjectButton = (
    <Tip label={t("project.new")} side="top" sideOffset={8} truncate={false} disableFocusTrigger>
      <span className="inline-flex">
        <Button
          aria-label={t("project.new")}
          className="h-8 w-7 shrink-0 p-0 text-muted-foreground hover:bg-transparent hover:text-primary"
          disabled={state.busy}
          onClick={(event) => {
            event.stopPropagation();
            requestCreate();
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <PlusIcon size={18} />
        </Button>
      </span>
    </Tip>
  );

  if (!collapsed) {
    return (
      <div className="relative mx-2">
        <div className={cn(
          "flex h-10 items-center overflow-hidden rounded-lg bg-background pl-4 pr-2 text-sm text-muted-foreground hover:bg-muted",
          active && "bg-muted text-primary",
        )}>
          <button
            aria-label={t("project.title")}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
            onClick={navigateWriter}
            type="button"
          >
            <span className="flex min-h-4 min-w-4 justify-center"><FileSignatureIcon size={16} /></span>
            <span className="min-w-0 truncate whitespace-nowrap">{t("project.title")}</span>
          </button>
          <div className="ml-auto flex shrink-0 items-center gap-0">
            {createProjectButton}
            <Tip
              label={projectsExpanded ? t("project.collapse") : t("project.expand")}
              side="top"
              sideOffset={8}
              truncate={false}
            >
              <Button
                aria-expanded={projectsExpanded}
                aria-label={projectsExpanded ? t("project.collapseList") : t("project.expandList")}
                className="h-8 w-6 shrink-0 text-muted-foreground hover:bg-transparent hover:text-primary"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setProjectsExpanded((value) => !value);
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <ChevronDownIcon
                  className={cn("transition-transform", projectsExpanded && "rotate-180")}
                  size={18}
                />
              </Button>
            </Tip>
          </div>
        </div>
        {projectsExpanded ? renderProjectList() : null}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative mx-auto w-10">
        <PopoverTrigger asChild openOnClick={false}>
          <button
            ref={triggerRef}
            aria-expanded={open}
            aria-label={t("project.title")}
            className={cn(
              "flex h-10 w-full items-center justify-center rounded-lg text-muted-foreground hover:bg-muted",
              active && "bg-muted text-primary",
            )}
            onClick={openWriter}
            type="button"
          >
            <FileSignatureIcon size={16} />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        align="start"
        alignOffset={-8}
        className="w-[165px] rounded-lg border bg-background p-2 shadow-sm"
        onKeyDown={handleMenuKeyDown}
        side="right"
        sideOffset={12}
      >
        <div className="flex h-10 items-center gap-3 rounded-lg px-2 text-sm text-muted-foreground">
          <FileSignatureIcon className="shrink-0" size={16} />
          <span className="min-w-0 flex-1 truncate">{t("project.title")}</span>
          {createProjectButton}
        </div>
        <div className="mt-1 border-t border-border pt-1">
          {renderProjectList(true)}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function WriterSidebarMenu(props: WriterSidebarMenuProps) {
  return (
    <I18nProvider>
      <WriterSidebarMenuContent {...props} />
    </I18nProvider>
  );
}
